import { useRef, useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const SPRING_CONFIG = { stiffness: 400, damping: 35, mass: 0.3 };
const OPACITY_DURATION_BASE = 0.5;
const OPACITY_DURATION_VARIATION = 0.15;
const OPACITY_EASE = [0.25, 0.1, 0.25, 1];
const OPACITY_DELAY_CYCLE = 1.2;
const OPACITY_DELAY_STEP = 0.015;
const MIN_OPACITY_MULTIPLIER = 0.5;
const MAX_OPACITY_MULTIPLIER = 1.5;
const MIN_OPACITY_FALLBACK = 0.3;
const PROXIMITY_MULTIPLIER = 1.2;
const PROXIMITY_OPACITY_BOOST = 0.8;

export function MouseEffectCard({
    style,
    theme = 'light',
    dotSize = 2,
    dotSpacing = 16,
    repulsionRadius = 80,
    repulsionStrength = 20,
    title = "EkahHealth",
    subtitle = "Your journey to natural wellness begins here",
    topText = "",
    topSubtext = "",
    primaryCtaText = "Get Started",
    primaryCtaUrl = "#",
    secondaryCtaText = "Learn More",
    secondaryCtaUrl = "#",
    footerText = "",
    onPrimaryClick,
    onSecondaryClick,
}) {
    const innerContainerRef = useRef(null);
    const mouseX = useMotionValue(Infinity);
    const mouseY = useMotionValue(Infinity);
    const [dots, setDots] = useState([]);

    useEffect(() => {
        const updateDots = () => {
            if (!innerContainerRef.current) return;
            const rect = innerContainerRef.current.getBoundingClientRect();
            const newDots = generateDots(rect.width, rect.height, dotSpacing);
            setDots(newDots);
        };

        updateDots();

        const resizeObserver = new ResizeObserver(updateDots);
        if (innerContainerRef.current) {
            resizeObserver.observe(innerContainerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [dotSpacing]);

    const handleMouseMove = (e) => {
        if (!innerContainerRef.current) return;

        const rect = innerContainerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        mouseX.set(x);
        mouseY.set(y);
    };

    const handleMouseLeave = () => {
        mouseX.set(Infinity);
        mouseY.set(Infinity);
    };

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            ...style
        }}>
            <div
                ref={innerContainerRef}
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '500px',
                    overflow: 'hidden',
                    padding: 0
                }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                {dots.map((dot, index) => (
                    <DotComponent
                        key={dot.id}
                        dot={dot}
                        index={index}
                        dotSize={dotSize}
                        mouseX={mouseX}
                        mouseY={mouseY}
                        repulsionRadius={repulsionRadius}
                        repulsionStrength={repulsionStrength}
                    />
                ))}

                {topText && (
                    <div style={{
                        position: 'absolute',
                        top: '24px',
                        left: '24px',
                        zIndex: 10
                    }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255, 255, 255, 0.6)',
                                filter: 'blur(16px)',
                                borderRadius: '8px'
                            }} />
                            <div style={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                <p style={{
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    color: theme === 'dark' ? '#f8fafc' : '#18181b',
                                    margin: 0
                                }}>
                                    {topText}
                                </p>
                                {topSubtext && (
                                    <p style={{
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        color: theme === 'dark' ? '#cbd5e1' : '#52525b',
                                        opacity: 0.7,
                                        margin: 0
                                    }}>
                                        {topSubtext}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div style={{
                    position: 'relative',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '0 16px'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '24px'
                    }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                                filter: 'blur(40px)',
                                borderRadius: '50%'
                            }} />
                            <h2 style={{
                                position: 'relative',
                                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                                fontWeight: 'bold',
                                color: theme === 'dark' ? '#f8fafc' : '#18181b',
                                textAlign: 'center',
                                letterSpacing: '-0.02em',
                                margin: 0
                            }}>
                                {title}
                            </h2>
                        </div>
                        {subtitle && (
                            <div style={{ position: 'relative' }}>
                                <div style={{
                                    position: 'absolute',
                                    inset: 0,
                                    background: theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                    filter: 'blur(24px)',
                                    borderRadius: '8px'
                                }} />
                                <p style={{
                                    position: 'relative',
                                    fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                                    color: theme === 'dark' ? '#cbd5e1' : '#3f3f46',
                                    textAlign: 'center',
                                    lineHeight: '1.6',
                                    maxWidth: '600px',
                                    fontWeight: '500',
                                    margin: 0
                                }}>
                                    {subtitle}
                                </p>
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginTop: '8px',
                            flexWrap: 'wrap',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (onPrimaryClick) onPrimaryClick();
                                    else if (primaryCtaUrl !== "#") window.location.href = primaryCtaUrl;
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '9999px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    padding: '12px 32px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                                }}
                            >
                                {primaryCtaText}
                            </button>
                            {secondaryCtaText && (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (onSecondaryClick) onSecondaryClick();
                                        else if (secondaryCtaUrl !== "#") window.location.href = secondaryCtaUrl;
                                    }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '9999px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        padding: '12px 32px',
                                        background: 'transparent',
                                        color: theme === 'dark' ? '#f8fafc' : '#18181b',
                                        border: `2px solid ${theme === 'dark' ? '#f8fafc' : '#18181b'}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = theme === 'dark' ? '#f8fafc' : '#18181b';
                                        e.currentTarget.style.color = theme === 'dark' ? '#18181b' : 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = theme === 'dark' ? '#f8fafc' : '#18181b';
                                    }}
                                >
                                    {secondaryCtaText}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {footerText && (
                    <div style={{
                        position: 'absolute',
                        bottom: '24px',
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                filter: 'blur(16px)',
                                borderRadius: '9999px'
                            }} />
                            <p style={{
                                position: 'relative',
                                fontSize: '12px',
                                color: theme === 'dark' ? '#cbd5e1' : '#52525b',
                                fontWeight: '500',
                                padding: '4px 16px',
                                margin: 0
                            }}>
                                {footerText}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function calculateDistance(x1, y1, x2, y2) {
    const dx = x1 - x2;
    const dy = y1 - y2;
    return Math.sqrt(dx * dx + dy * dy);
}

function generateDots(width, height, spacing) {
    const dots = [];
    const cols = Math.ceil(width / spacing);
    const rows = Math.ceil(height / spacing);
    const centerX = width / 2;
    const centerY = height / 2;
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);

    for (let row = 0; row <= rows; row++) {
        for (let col = 0; col <= cols; col++) {
            const x = col * spacing;
            const y = row * spacing;

            const dx = x - centerX;
            const dy = y - centerY;
            const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

            const edgeFactor = Math.min(distanceFromCenter / (maxDistance * 0.7), 1);

            if (Math.random() > edgeFactor) {
                continue;
            }

            const pattern = (row + col) % 3;
            const baseOpacities = [0.3, 0.5, 0.7];
            const opacity = baseOpacities[pattern] * edgeFactor;

            dots.push({
                id: `dot-${row}-${col}`,
                x,
                y,
                baseX: x,
                baseY: y,
                opacity,
            });
        }
    }

    return dots;
}

function DotComponent({
    dot,
    index,
    dotSize,
    mouseX,
    mouseY,
    repulsionRadius,
    repulsionStrength,
}) {
    const posX = useTransform([mouseX, mouseY], () => {
        const mx = mouseX.get();
        const my = mouseY.get();

        if (!Number.isFinite(mx) || !Number.isFinite(my)) {
            return 0;
        }

        const dx = dot.baseX - mx;
        const dy = dot.baseY - my;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < repulsionRadius) {
            const force = (1 - distance / repulsionRadius) * repulsionStrength;
            const angle = Math.atan2(dy, dx);
            return Math.cos(angle) * force;
        }

        return 0;
    });

    const posY = useTransform([mouseX, mouseY], () => {
        const mx = mouseX.get();
        const my = mouseY.get();

        if (!Number.isFinite(mx) || !Number.isFinite(my)) {
            return 0;
        }

        const dx = dot.baseX - mx;
        const dy = dot.baseY - my;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < repulsionRadius) {
            const force = (1 - distance / repulsionRadius) * repulsionStrength;
            const angle = Math.atan2(dy, dx);
            return Math.sin(angle) * force;
        }

        return 0;
    });

    const opacityBoost = useTransform([mouseX, mouseY], () => {
        const mx = mouseX.get();
        const my = mouseY.get();

        if (!Number.isFinite(mx) || !Number.isFinite(my)) return 0;

        const distance = calculateDistance(dot.baseX, dot.baseY, mx, my);
        const maxDistance = repulsionRadius * PROXIMITY_MULTIPLIER;

        if (distance < maxDistance) {
            const proximityFactor = 1 - distance / maxDistance;
            return proximityFactor * PROXIMITY_OPACITY_BOOST;
        }

        return 0;
    });

    const x = useSpring(posX, SPRING_CONFIG);
    const y = useSpring(posY, SPRING_CONFIG);

    const baseMinOpacity = Math.max(
        dot.opacity * MIN_OPACITY_MULTIPLIER,
        MIN_OPACITY_FALLBACK
    );
    const baseMaxOpacity = Math.min(dot.opacity * MAX_OPACITY_MULTIPLIER, 1);

    const minOpacityWithBoost = useTransform(opacityBoost, (boost) =>
        Math.min(baseMinOpacity + boost, 1)
    );

    const delay = (index * OPACITY_DELAY_STEP) % OPACITY_DELAY_CYCLE;

    return (
        <motion.div
            style={{
                position: 'absolute',
                borderRadius: '50%',
                background: '#a1a1aa',
                width: dotSize,
                height: dotSize,
                left: dot.baseX,
                top: dot.baseY,
                x,
                y,
                opacity: useSpring(minOpacityWithBoost, {
                    stiffness: 150,
                    damping: 25,
                }),
                willChange: 'transform',
            }}
            initial={{ opacity: baseMinOpacity }}
            animate={{
                opacity: [baseMinOpacity, baseMaxOpacity, baseMinOpacity],
            }}
            transition={{
                opacity: {
                    duration:
                        OPACITY_DURATION_BASE +
                        (index % 4) * OPACITY_DURATION_VARIATION,
                    repeat: Infinity,
                    ease: OPACITY_EASE,
                    delay,
                    times: [0, 0.5, 1],
                },
            }}
        />
    );
}

export default MouseEffectCard;
